import { useMemo, useRef, useState } from "react";

import axios from "axios";
import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "constants/index";
import {
  useCompanyMembers,
  useRoleContext,
  useSwrFetch,
  useWriterComponentState,
} from "hooks";
import {
  shareShipmentTemplateUrl,
  shipmentTemplateShareesUrl,
} from "utils/templates";

import { ShareShipmentTemplateModal } from "./ShareShipmentTemplateModal";
import {
  buildShareConfirmationMessage,
  listSelectableTeammates,
} from "./utils";

import type {
  ShareShipmentTemplateResponse,
  ShipmentTemplate,
  ShipmentTemplateShareesResponse,
} from "types/shipment";

const SHAREES_LOAD_ERROR = "Failed to load who this template was shared with.";
// A 400 means a recipient is no longer an enabled teammate.
const RECIPIENTS_REJECTED_ERROR =
  "Some of the selected teammates can no longer receive this template. Refresh the page and try again.";

interface Props {
  template: ShipmentTemplate;
  onClose: VoidFunction;
  onShared: (confirmationMessage: string) => void;
}

export function ShareShipmentTemplateModalContainer(props: Props) {
  const { template, onClose, onShared } = props;
  const { role } = useRoleContext();
  const posthog = usePostHog();
  const {
    members,
    loading: membersLoading,
    error: membersError,
  } = useCompanyMembers();
  const {
    data: sharees,
    loading: shareesLoading,
    error: shareesError,
    mutate: mutateSharees,
  } = useSwrFetch<ShipmentTemplateShareesResponse>({
    url: role ? shipmentTemplateShareesUrl(role.id, template.id) : null,
    errorMessage: SHAREES_LOAD_ERROR,
  });
  // A ref, unlike the `sharing` state, is already set when a second click lands
  // before React has re-rendered.
  const shareInFlight = useRef<boolean>(false);
  const [recipientsRejected, setRecipientsRejected] = useState<boolean>(false);
  const {
    loading: sharing,
    error: shareError,
    write,
  } = useWriterComponentState({
    errorMessage:
      "Failed to share the template. Please contact us if the issue persists.",
    writeConfig: { method: "post", url: shareShipmentTemplateUrl(template.id) },
    onWriteFailure: (e) => {
      setRecipientsRejected(
        axios.isAxiosError(e) && e.response?.status === 400,
      );
    },
    onWriteFinally: () => {
      shareInFlight.current = false;
    },
  });

  const roleId = role?.id;
  const teammates = useMemo(
    () =>
      roleId === undefined ? [] : listSelectableTeammates(members, roleId),
    [members, roleId],
  );

  if (!role) return null;

  const sharedRoleIds =
    sharees?.shares.find((share) => share.template_id === template.id)
      ?.recipient_role_ids ?? null;

  const loading = membersLoading || shareesLoading;
  // A successful response always has an entry for the requested template, so a
  // missing one is a failed load, not an empty list.
  const missingShareesError =
    !loading && sharedRoleIds === null ? SHAREES_LOAD_ERROR : null;

  const onShare = (recipientRoleIds: number[], onSuccess: VoidFunction) => {
    if (shareInFlight.current) return;
    shareInFlight.current = true;
    setRecipientsRejected(false);
    const recipientIds = new Set(recipientRoleIds);
    write<ShareShipmentTemplateResponse>(
      () => {
        posthog?.capture(ClientAppEvent.SHIPMENT_TEMPLATE_SHARED, {
          role,
          template_id: template.id,
          recipient_count: recipientRoleIds.length,
        });
        // Shows "Shared" right away; the revalidation that follows confirms it.
        mutateSharees({
          shares: [
            {
              template_id: template.id,
              recipient_role_ids: [
                ...new Set([...(sharedRoleIds ?? []), ...recipientRoleIds]),
              ],
            },
          ],
        });
        onSuccess();
        onShared(
          buildShareConfirmationMessage(
            template.name,
            teammates.filter((teammate) => recipientIds.has(teammate.id)),
          ),
        );
      },
      { recipient_role_ids: recipientRoleIds },
    );
  };

  return (
    <ShareShipmentTemplateModal
      template={template}
      teammates={teammates}
      callerBranchId={role.branch}
      sharedRoleIds={sharedRoleIds}
      loading={loading}
      error={membersError ?? shareesError ?? missingShareesError}
      sharing={sharing}
      shareError={recipientsRejected ? RECIPIENTS_REJECTED_ERROR : shareError}
      onShare={onShare}
      onClose={onClose}
    />
  );
}
